import { Close, Event, CalendarMonth } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import { consultantScheduleMonitoringDate } from '../../../../../helpers/api';
import { openNotification } from '../../../../../helpers/notification';

export default function SchedulerDialog({ open, onClose, facilityId, quarter, year }) {
  // --- CORE FUNCTIONALITY (UNTOUCHED) ---
  const initDate =
    quarter?.scheduleDate?.split('T')[0] === '0001-01-01'
      ? ''
      : quarter?.scheduleDate?.split('T')[0] || '';
  const [saving, setSaving] = useState(false);
  const [scheduleValue, setScheduleValue] = useState(initDate);

  const handleSchedule = async () => {
    setSaving(true);
    try {
      const payload = {
        quarter: quarter?.reportQuarter,
        facilityId: facilityId,
        reportId: quarter?.reportId,
        scheduleDate: scheduleValue,
      };

      const res = await consultantScheduleMonitoringDate(payload);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        openNotification({ type: 'success', message: status?.desc });
        onClose();
      }
    } catch (error) {
      const errmsg = error?.data?.result?.status?.desc || 'Something went wrong. Please try again';
      openNotification({ type: 'error', message: errmsg });
    } finally {
      setSaving(false);
    }
  };
  // --------------------------------------

  return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px', // Modern rounded dialog
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.08)', // Soft, deep shadow
          p: 1, // Slight inner padding for breathing room
        }
      }}
    >
      {/* HEADER SECTION */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              p: 1,
              borderRadius: '10px',
              bgcolor: alpha('#1976d2', 0.1),
              color: '#1976d2',
            }}
          >
            <Event />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Schedule Monitoring
          </Typography>
        </DialogTitle>
        <IconButton 
          onClick={() => onClose()} 
          disabled={saving}
          sx={{
            bgcolor: alpha('#9e9e9e', 0.1),
            '&:hover': { bgcolor: alpha('#9e9e9e', 0.2) }
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* CONTENT SECTION */}
      <DialogContent sx={{ pt: '10px !important' }}>
        <Alert 
          severity="info" 
          icon={<CalendarMonth fontSize="inherit" />}
          sx={{ 
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'info.light',
            bgcolor: alpha('#0288d1', 0.05),
            mb: 3,
            alignItems: 'center'
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            Set the monitoring date for <strong>Quarter {quarter?.reportQuarter}, {quarter?.reportYear || year}</strong>.
          </Typography>
        </Alert>
        
        <Grid2 container spacing={2}>
          <Grid2 size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
              Select Date
            </Typography>
            <TextField
              fullWidth
              value={scheduleValue}
              type="date"
              onChange={(e) => setScheduleValue(e.target.value)}
              disabled={saving}
              InputProps={{
                sx: { 
                  borderRadius: '12px', 
                  bgcolor: '#f9fafb',
                  '&:hover': { bgcolor: '#f4f6f8' }
                }
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      {/* ACTIONS SECTION */}
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={() => onClose()}
          disabled={saving}
          sx={{ 
            textTransform: 'none', 
            borderRadius: '10px',
            fontWeight: 600,
            color: 'text.secondary',
            '&:hover': { bgcolor: alpha('#9e9e9e', 0.1) }
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disabled={saving} 
          startIcon={saving && <CircularProgress size={20} color="inherit" />}
          onClick={handleSchedule}
          sx={{ 
            textTransform: 'none', 
            borderRadius: '10px',
            px: 4,
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { boxShadow: '0px 4px 12px rgba(25, 118, 210, 0.2)' }
          }}
        >
          {saving ? 'Saving...' : 'Save Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}